/**
 * Correct the trips on an issued invoice.
 *
 * Every field can be edited, but they do not all do the same thing, and
 * the screen says which is which before anything is saved:
 *
 *   - truck, date, DO, weight / way bill, from, to, remarks are saved on
 *     the trip; the invoice keeps its number, totals and QR;
 *   - a new rate issues a debit or credit note against the invoice;
 *   - a new customer credits the trip off this invoice and leaves it
 *     unbilled for them.
 *
 * Only what was actually changed is sent. The amounts on the notes come
 * back from the server -- the figure posted is the figure shown.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, MenuItem, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import {
  correctTripInvoice, downloadInvoiceNotePdf, fetchTripInvoice,
  fetchVehicles,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import type {
  InvoiceNote, TripCorrection, TripInvoice, TripInvoiceLine,
} from '../api/types'
import { useLedgers } from '../store/LedgerCache'
import { c, fmtMoney } from '../theme'
import { normalise, sanitize } from '../vouchers/money'
import { sanitizeWeight, useTripRef } from './tripRef'

interface Row {
  trip_id: number
  trip_date: string
  vehicle_plate: string
  do_number: string
  ref: string                // weight or way bill, per company
  from_place: string
  to_place: string
  rate: string
  remarks: string
  customer_code: string
}

function fromLine(l: TripInvoiceLine, weight: boolean): Row {
  return {
    trip_id: l.trip_id,
    trip_date: l.trip_date,
    vehicle_plate: l.vehicle,
    do_number: l.do_number ?? '',
    ref: (weight ? l.weight_kg : l.way_bill) ?? '',
    from_place: l.from_place,
    to_place: l.to_place,
    rate: l.current_rate ?? l.rate,
    remarks: l.remarks ?? '',
    customer_code: l.customer_code ?? '',
  }
}

/** What changed on one row, in the shape the server takes. */
export function diffRow(before: Row, after: Row, weight: boolean): TripCorrection | null {
  const out: TripCorrection = { trip_id: after.trip_id }
  const text = (k: 'do_number' | 'remarks') =>
    after[k].trim() !== before[k].trim()
  if (after.trip_date !== before.trip_date) out.trip_date = after.trip_date
  if (after.vehicle_plate !== before.vehicle_plate) out.vehicle_plate = after.vehicle_plate
  if (text('do_number')) out.do_number = after.do_number.trim() || null
  if (text('remarks')) out.remarks = after.remarks.trim() || null
  if (after.from_place.trim() !== before.from_place.trim()) out.from_place = after.from_place.trim()
  if (after.to_place.trim() !== before.to_place.trim()) out.to_place = after.to_place.trim()
  if (weight) {
    const a = after.ref.trim(), b = before.ref.trim()
    if (a === '' ? b !== '' : b === '' || Number(a) !== Number(b))
      out.weight_kg = a || null
  } else if (after.ref.trim() !== before.ref.trim()) {
    out.way_bill = after.ref.trim() || null
  }
  if (normalise(after.rate) !== normalise(before.rate)) out.rate = normalise(after.rate)
  if (after.customer_code !== before.customer_code) out.customer_code = after.customer_code
  return Object.keys(out).length > 1 ? out : null
}

export type Effect = 'none' | 'details' | 'debit' | 'credit' | 'move'

/** Which kind of document a row's change will produce. */
export function effect(ch: TripCorrection | null, before: Row): Effect {
  if (!ch) return 'none'
  if (ch.customer_code) return 'move'
  if (ch.rate !== undefined) {
    return Number(ch.rate) > Number(before.rate) ? 'debit' : 'credit'
  }
  return 'details'
}

export default function CorrectInvoiceDialog({ invoice, onClose }: {
  invoice: TripInvoice
  onClose: () => void
}) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const weight = useTripRef() === 'WEIGHT'
  const { ledgers } = useLedgers()

  const detail = useQuery({ queryKey: ['trip-invoice', invoice.id],
    queryFn: () => fetchTripInvoice(invoice.id) })
  const vehicles = useQuery({ queryKey: ['vehicles'],
    queryFn: () => fetchVehicles() })

  const customers = useMemo(() => ledgers.filter((l) =>
    (l.group_keys ?? [l.group_key]).includes('DEBTORS')), [ledgers])

  const original = useMemo(() => (detail.data?.lines ?? [])
    .filter((l) => l.on_invoice !== false)
    .map((l) => fromLine(l, weight)), [detail.data, weight])
  const [rows, setRows] = useState<Row[]>([])
  useEffect(() => { setRows(original) }, [original])

  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<TripInvoice | null>(null)

  const changes = rows.map((r, i) => diffRow(original[i], r, weight))
  const effects = changes.map((ch, i) => effect(ch, original[i]))
  const sent = changes.filter((x): x is TripCorrection => x !== null)
  const needsReason = effects.some((e) => e === 'debit' || e === 'credit'
                                         || e === 'move')
  const ready = sent.length > 0 && (!needsReason || !!reason.trim())

  const set = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)))

  const save = useMutation({
    mutationFn: () => correctTripInvoice(invoice.id, {
      trips: sent, reason: reason.trim() || undefined }),
    onSuccess: (res) => {
      setError(null)
      setDone(res)
      void qc.invalidateQueries({ queryKey: ['trip-invoices'] })
      void qc.invalidateQueries({ queryKey: ['trip-invoice', invoice.id] })
      void qc.invalidateQueries({ queryKey: ['trips'] })
      void qc.invalidateQueries({ queryKey: ['outstanding'] })
    },
    onError: (e) => setError(errorMessage(e)),
  })

  if (done) {
    return (
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('correct.doneTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1.5 }}>
            {t('correct.doneBody', { count: done.changes ?? 0,
                                     no: invoice.invoice_no })}
          </Typography>
          {(done.new_notes ?? []).length === 0 ? (
            <Typography variant="body2" sx={{ color: c.inkMuted }}>
              {t('correct.noNotes')}
            </Typography>
          ) : (done.new_notes ?? []).map((n) => (
            <NoteRow key={n.id} note={n} />
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>
    )
  }

  const cell = { px: 0.5, py: 0.5 }
  const input = { '& .MuiInputBase-input': { fontSize: 12.5, py: 0.6 } }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle sx={{ pb: 0.5 }}>
        {t('correct.title', { no: invoice.invoice_no })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: c.inkMuted, mb: 1.5 }}>
          {t('correct.explain')}
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        {detail.isLoading ? <><Skeleton /><Skeleton /></> : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box component="table" sx={{ borderCollapse: 'collapse',
                                         minWidth: 1100, width: '100%' }}>
              <Box component="thead">
                <Box component="tr">
                  {[t('entry.date'), t('trip.vehicle'), t('trip.doNumber'),
                    weight ? t('trip.weightKg') : t('trip.wayBill'),
                    t('trip.from'), t('trip.to'), t('trip.rate'),
                    t('trip.remarks'), t('trip.customer'), ''].map((h, i) => (
                    <Box component="th" key={i}
                      sx={{ textAlign: 'start', ...cell }}>
                      <span className="section-label">{h}</span>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {rows.map((r, i) => (
                  <Box component="tr" key={r.trip_id}
                    sx={{ borderTop: `1px solid ${c.divider}`,
                          bgcolor: effects[i] === 'none' ? undefined
                            : c.primarySoft }}>
                    <Box component="td" sx={{ ...cell, width: 140 }}>
                      <TextField size="small" type="date" value={r.trip_date}
                        sx={input} inputProps={{ 'aria-label': t('entry.date') }}
                        onChange={(e) => set(i, { trip_date: e.target.value })} />
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 120 }}>
                      <TextField select size="small" value={r.vehicle_plate}
                        fullWidth sx={input}
                        inputProps={{ 'aria-label': t('trip.vehicle') }}
                        onChange={(e) => set(i, { vehicle_plate: e.target.value })}>
                        {(vehicles.data ?? [])
                          .filter((v) => v.is_active || v.plate_no === r.vehicle_plate)
                          .map((v) => (
                            <MenuItem key={v.id} value={v.plate_no}>{v.plate_no}</MenuItem>
                          ))}
                        {!(vehicles.data ?? []).some((v) => v.plate_no === r.vehicle_plate) && (
                          <MenuItem value={r.vehicle_plate}>{r.vehicle_plate}</MenuItem>
                        )}
                      </TextField>
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 100 }}>
                      <TextField size="small" value={r.do_number} sx={input}
                        inputProps={{ 'aria-label': t('trip.doNumber') }}
                        onChange={(e) => set(i, { do_number: e.target.value })} />
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 110 }}>
                      <TextField size="small" value={r.ref} sx={input}
                        inputProps={{ 'aria-label': weight ? t('trip.weightKg') : t('trip.wayBill'),
                                      style: weight ? { textAlign: 'right' } : undefined }}
                        onChange={(e) => set(i, { ref: weight
                          ? sanitizeWeight(e.target.value) : e.target.value })} />
                    </Box>
                    <Box component="td" sx={cell}>
                      <TextField size="small" value={r.from_place} sx={input}
                        inputProps={{ 'aria-label': t('trip.from') }}
                        onChange={(e) => set(i, { from_place: e.target.value })} />
                    </Box>
                    <Box component="td" sx={cell}>
                      <TextField size="small" value={r.to_place} sx={input}
                        inputProps={{ 'aria-label': t('trip.to') }}
                        onChange={(e) => set(i, { to_place: e.target.value })} />
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 100 }}>
                      <TextField size="small" value={r.rate} sx={input}
                        inputProps={{ 'aria-label': t('trip.rate'),
                                      style: { textAlign: 'right' } }}
                        onChange={(e) => set(i, { rate: sanitize(e.target.value) })}
                        onBlur={() => set(i, { rate: normalise(r.rate) })} />
                    </Box>
                    <Box component="td" sx={cell}>
                      <TextField size="small" value={r.remarks} sx={input}
                        inputProps={{ 'aria-label': t('trip.remarks') }}
                        onChange={(e) => set(i, { remarks: e.target.value })} />
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 200 }}>
                      <TextField select size="small" value={r.customer_code}
                        fullWidth sx={input}
                        inputProps={{ 'aria-label': t('trip.customer') }}
                        onChange={(e) => set(i, { customer_code: e.target.value })}>
                        {customers.map((l) => (
                          <MenuItem key={l.code} value={l.code}>
                            {l.code} {l.name_en}
                          </MenuItem>
                        ))}
                        {!customers.some((l) => l.code === r.customer_code) && (
                          <MenuItem value={r.customer_code}>{r.customer_code}</MenuItem>
                        )}
                      </TextField>
                    </Box>
                    <Box component="td" sx={{ ...cell, width: 130 }}>
                      <EffectChip effect={effects[i]} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        )}

        <TextField fullWidth size="small" sx={{ mt: 2 }} value={reason}
          label={t('correct.reason')}
          required={needsReason}
          helperText={needsReason ? t('correct.reasonNeeded') : t('correct.reasonOptional')}
          onChange={(e) => setReason(e.target.value)}
          inputProps={{ maxLength: 300 }} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Typography variant="body2" sx={{ flex: 1, color: c.inkMuted }}>
          {t('correct.changedCount', { count: sent.length })}
        </Typography>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!ready || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? t('common.loading') : t('correct.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function EffectChip({ effect }: { effect: Effect }) {
  const { t } = useTranslation()
  if (effect === 'none') return null
  const tone = effect === 'details' ? { bgcolor: c.divider, color: c.inkSecondary }
    : effect === 'debit' ? { bgcolor: c.warningSoft, color: c.warningInk }
      : { bgcolor: c.dangerSoft, color: c.dangerInk }
  return <Chip size="small" label={t(`correct.effect.${effect}`)}
    sx={{ height: 20, fontSize: 10.5, ...tone }} />
}
export function NoteRow({ note }: { note: InvoiceNote }) {
  const { t } = useTranslation()
  return (
    <Stack direction="row" alignItems="center" spacing={1}
      sx={{ py: 0.75, borderTop: `1px solid ${c.divider}` }}>
      <Chip size="small" sx={{ height: 20, fontSize: 10.5 }}
        label={note.kind === 'CREDIT' ? t('correct.credit') : t('correct.debit')} />
      <Typography className="num" sx={{ fontSize: 13, flex: 1 }}>
        {note.note_no}
      </Typography>
      <Typography className="num" sx={{ fontSize: 13 }}>
        {note.kind === 'CREDIT' ? '−' : '+'}{fmtMoney(note.total)}
      </Typography>
      <Button size="small" startIcon={<PictureAsPdfIcon />}
        onClick={() => void downloadInvoiceNotePdf(note)}>PDF</Button>
    </Stack>
  )
}
